// src/components/layout/AppSidebar.js
import { useCallback, useMemo, useEffect, useRef, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useIsMobile } from "../../hooks/useIsMobile";
import { useAuth } from "../../context/AuthContext";
import { getAvatarById } from "../../data/avataresPredefinidos";
import api from "../../api/client";
import logo from "../../assets/vadieg_logo.svg";
import {
  Home, ClipboardList, Ticket, Wrench, AlertTriangle, UserX,
  Settings, Newspaper, Calendar, Users,
  BookOpen, GraduationCap, LayoutDashboard, BarChart3, BarChart2,
  Monitor, Shield, User, LogOut, TreePalm, UserCheck,
} from 'lucide-react';

const BG     = '#0d1117';
const BORDER = '#1e293b';
const TEXT   = '#ffffff';
const MUTED  = '#64748b';
const NAV    = '#e8edf5';  // ítems de navegación en reposo
const ACCENT = '#f59e0b';

function getInitials(name) {
  if (!name) return 'U';
  return name.split(' ').map(n => n[0]).join('').substring(0, 2).toUpperCase();
}

function UserAvatar({ usuario, size = 36 }) {
  const initials = getInitials(usuario?.full_name);
  if (usuario?.avatar?.startsWith('predefined:')) {
    const data = getAvatarById(usuario.avatar.replace('predefined:', ''));
    if (data) return (
      <div style={{
        width: size, height: size, borderRadius: '8px', background: data.bg, flexShrink: 0,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        fontSize: data.type === 'gradient' ? '0.75rem' : '1rem',
        fontWeight: data.type === 'gradient' ? 700 : 400, color: '#fff',
      }}>
        {data.type === 'gradient' ? initials : data.value}
      </div>
    );
  }
  if (usuario?.avatar?.includes('/uploads/')) return (
    <img
      src={`${process.env.REACT_APP_API_URL?.replace('/api', '')}${usuario.avatar}`}
      alt={usuario?.full_name}
      style={{ width: size, height: size, borderRadius: '8px', objectFit: 'cover', flexShrink: 0 }}
    />
  );
  return (
    <div style={{
      width: size, height: size, borderRadius: '8px', flexShrink: 0,
      background: 'linear-gradient(135deg, #fbbf24 0%, #ea580c 100%)',
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      fontSize: '0.75rem', fontWeight: 700, color: '#000',
    }}>
      {initials}
    </div>
  );
}

function SectionLabel({ children }) {
  return (
    <div style={{
      fontSize: '0.58rem', fontWeight: 700, letterSpacing: '0.12em',
      textTransform: 'uppercase', color: '#8b9ab3',
      padding: '10px 12px 3px',
    }}>
      {children}
    </div>
  );
}

function NavItem({ to, icon: Icon, label, badge = 0 }) {
  const { pathname } = useLocation();
  const active = to === '/' ? pathname === '/' : pathname.startsWith(to);
  return (
    <Link
      to={to}
      style={{
        display: 'flex', alignItems: 'center', gap: '8px',
        padding: '5px 10px', borderRadius: '6px',
        textDecoration: 'none', fontSize: '0.8rem', fontWeight: 400,
        color: active ? TEXT : NAV,
        background: active ? 'rgba(251,191,36,0.1)' : 'transparent',
        borderLeft: `2px solid ${active ? ACCENT : 'transparent'}`,
        transition: 'all 0.12s',
        marginBottom: '1px',
      }}
      onMouseEnter={e => {
        if (!active) {
          e.currentTarget.style.background = '#1a2236';
          e.currentTarget.style.color = TEXT;
        }
      }}
      onMouseLeave={e => {
        if (!active) {
          e.currentTarget.style.background = 'transparent';
          e.currentTarget.style.color = NAV;
        }
      }}
    >
      <span style={{ flexShrink: 0, width: 16, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        {Icon && <Icon size={14} strokeWidth={1.8} />}
      </span>
      <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{label}</span>
      {badge > 0 && (
        <span title={`${badge} pendiente${badge === 1 ? '' : 's'}`}
          style={{ marginLeft: 'auto', flexShrink: 0, minWidth: 18, height: 18, padding: '0 5px', borderRadius: 9,
            background: ACCENT, color: '#1f2937', fontSize: '0.68rem', fontWeight: 800,
            display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          {badge > 99 ? '99+' : badge}
        </span>
      )}
    </Link>
  );
}

export default function AppSidebar({ mobileOpen, onClose, oculto = false, onOcultar }) {
  const { usuario, logout, tieneAcceso, esAdmin: esAdminFn } = useAuth();
  const navigate = useNavigate();
  const isMobile = useIsMobile();
  const { pathname } = useLocation();
  const prevPath = useRef(pathname);

  // Cerrar sidebar al navegar en mobile
  useEffect(() => {
    if (pathname !== prevPath.current) {
      prevPath.current = pathname;
      if (isMobile) onClose?.();
    }
  }, [pathname, isMobile, onClose]);
  const esAdmin = esAdminFn();

  // Portal de Vacaciones: lo ve quien tiene legajo de UECARA o fuera de convenio
  // (y RRHH). Depende del legajo de la persona, no de un acceso del rol.
  // A los jefes, además, el número de pedidos que esperan su aprobación: se
  // actualiza cada 2 minutos y al cambiar de pantalla (por ejemplo, después de aprobar).
  const [verVacaciones, setVerVacaciones] = useState(false);
  const [pendientesVacaciones, setPendientesVacaciones] = useState(0);
  useEffect(() => {
    if (!usuario) return undefined;
    const leer = () => api.get('/vacaciones/yo')
      .then(r => {
        setVerVacaciones(!!r.data?.habilitado);
        setPendientesVacaciones(Number(r.data?.pendientes_aprobar) || 0);
      })
      .catch(() => setVerVacaciones(false));
    leer();
    const t = setInterval(leer, 120000);
    return () => clearInterval(t);
  }, [usuario, pathname]);

  // Ausentismo: a quien valida (RRHH / admin), cuántos registros esperan validación.
  const validaAusentismo = esAdmin || tieneAcceso('hr.reports:read');
  const [pendientesAusentismo, setPendientesAusentismo] = useState(0);
  useEffect(() => {
    if (!usuario || !validaAusentismo) { setPendientesAusentismo(0); return undefined; }
    const leer = () => api.get('/ausentismo/pendientes')
      .then(r => setPendientesAusentismo(Number(r.data?.total) || 0))
      .catch(() => setPendientesAusentismo(0));
    leer();
    const t = setInterval(leer, 120000);
    return () => clearInterval(t);
  }, [usuario, validaAusentismo, pathname]);

  const handleLogout = useCallback(async () => {
    try { await api.post('/logout', { refreshToken: localStorage.getItem('refreshToken') || undefined }).catch(() => {}); } finally {
      logout();
      navigate('/login');
    }
  }, [logout, navigate]);

  const operaciones = useMemo(() => [
    // El maquinista (solo partes de maquinista, sin partes de obra) entra directo a su lista.
    { to: (esAdmin || tieneAcceso('ops.site-logs:read') || tieneAcceso('ops.site-logs:create')) ? '/partes-obra' : '/partes/lista',
      icon: ClipboardList, label: 'Producción',          show: tieneAcceso('ops.site-logs:read') || tieneAcceso('ops.site-logs:create') || tieneAcceso('ops.machinist-logs:create') || tieneAcceso('ops.machinist-logs:read:own') || esAdmin },
    { to: '/tickets',          icon: Ticket,        label: 'Tickets',              show: tieneAcceso('shared.tickets:read:own') || esAdmin },
    { to: '/ordenes/lista',    icon: Wrench,        label: 'Órdenes de Servicio',  show: tieneAcceso('ops.work-orders:read') || esAdmin },
    { to: '/hallazgos',        icon: AlertTriangle, label: 'Hallazgos y Desvíos', show: tieneAcceso('hse.findings:read') || tieneAcceso('hse.findings:write') || esAdmin },
    { to: '/ausentismo',       icon: UserX,         label: 'Ausentismo',           show: tieneAcceso('hr.attendance:read') || tieneAcceso('hr.attendance:write') || esAdmin, badge: pendientesAusentismo },
    // Control de Personal (ROL-15): entra directo a la Planilla de Control.
    // ops.sites:read:all lo tiene solo ese rol; el resto llega desde Producción.
    { to: '/partes-obra/personal', icon: UserCheck, label: 'Control de Personal',  show: tieneAcceso('ops.sites:read:all') },
    { to: '/vacaciones',       icon: TreePalm,      label: 'Portal de Vacaciones', show: verVacaciones || esAdmin, badge: pendientesVacaciones },
    { to: '/servicios',        icon: Settings,      label: 'Servicios Generales',  show: true },
    // Remitos Internos: oculto por ahora (oct 2026)
    // { to: '/remitos',          icon: Package,       label: 'Remitos Internos',     show: esAdmin },
  ].filter(i => i.show), [tieneAcceso, esAdmin, verVacaciones, pendientesVacaciones, pendientesAusentismo]);

  const informacion = useMemo(() => [
    { to: '/novedades',    icon: Newspaper,      label: 'Novedades',       show: true },
    { to: '/agenda',       icon: Calendar,       label: 'Agenda',          show: tieneAcceso('shared.agenda:read') || esAdmin },
    { to: '/contactos',    icon: Users,          label: 'Contactos',       show: tieneAcceso('shared.directory:read') || esAdmin },
    { to: '/biblioteca',   icon: BookOpen,       label: 'Biblioteca',      show: true },
    { to: '/capacitacion', icon: GraduationCap,  label: 'Capacitación',    show: tieneAcceso('shared.training:read') || esAdmin },
  ].filter(i => i.show), [tieneAcceso, esAdmin]);

  const gestion = useMemo(() => [
    { to: '/dashboard-ejecutivo',  icon: LayoutDashboard, label: 'Dashboard Ejecutivo',  show: tieneAcceso('reports.executive:read') || tieneAcceso('exec.dashboard:read') || esAdmin },
    // el área de pagos (solo exec.finance-documents:read) entra directo a Finanzas › Documentos
    { to: (tieneAcceso('exec.dashboard:manage') || esAdmin) ? '/gerencial' : '/gerencial/finanzas/documentos',
      icon: BarChart3,       label: 'Gerencial',            show: tieneAcceso('exec.dashboard:manage') || tieneAcceso('exec.finance-documents:read') || esAdmin },
    { to: '/reportes',             icon: BarChart2,       label: 'Reportes',             show: esAdmin },
    { to: '/equipos-informaticos', icon: Monitor,         label: 'Equipos Informáticos', show: esAdmin },
    { to: '/admin',                icon: Shield,          label: 'Administración',       show: esAdmin },
  ].filter(i => i.show), [tieneAcceso, esAdmin]);

  if (!usuario) return null;

  return (
    <>
      {/* Overlay backdrop en mobile */}
      {isMobile && mobileOpen && (
        <div
          onClick={onClose}
          style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.55)', zIndex: 989 }}
        />
      )}

    <aside style={{
      position: 'fixed', top: 0, left: 0, width: '240px', height: '100vh',
      background: BG, borderRight: `1px solid ${BORDER}`,
      display: 'flex', flexDirection: 'column', zIndex: 990,
      overflowY: 'auto', scrollbarWidth: 'none',
      transform: isMobile ? `translateX(${mobileOpen ? 0 : -240}px)` : (oculto ? 'translateX(-240px)' : 'none'),
      transition: 'transform 0.25s ease',
      boxShadow: isMobile && mobileOpen ? '4px 0 24px rgba(0,0,0,0.5)' : 'none',
    }}>
      <style>{`aside::-webkit-scrollbar{display:none}`}</style>

      {/* Logo + botón cerrar en mobile */}
      <div style={{ padding: '10px 14px', borderBottom: `1px solid ${BORDER}`, flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <Link to="/" style={{ display: 'flex', alignItems: 'center', gap: 7, textDecoration: 'none' }}>
          <img src={logo} alt="VADIEG" style={{ height: 26 }} />
        </Link>
        {isMobile ? (
          <button onClick={onClose} style={{ background: 'none', border: 'none', color: MUTED, cursor: 'pointer', fontSize: 18, padding: '2px 6px', lineHeight: 1 }}>✕</button>
        ) : onOcultar && (
          <button onClick={onOcultar} title="Ocultar el menú" aria-label="Ocultar el menú"
            style={{ height: 'auto', background: 'none', border: `1px solid ${BORDER}`, borderRadius: 6, color: MUTED, cursor: 'pointer', fontSize: 13, padding: '2px 7px', lineHeight: 1.2 }}>
            «
          </button>
        )}
      </div>

      {/* Usuario */}

      {/* Nav */}
      <nav style={{ flex: 1, padding: '4px 8px 0', overflowY: 'auto', scrollbarWidth: 'none' }}>
        <SectionLabel>Principal</SectionLabel>
        <NavItem to="/" icon={Home} label="Inicio" />

        {operaciones.length > 0 && <>
          <SectionLabel>Operaciones</SectionLabel>
          {operaciones.map(i => <NavItem key={i.to} to={i.to} icon={i.icon} label={i.label} badge={i.badge} />)}
        </>}

        {informacion.length > 0 && <>
          <SectionLabel>Información</SectionLabel>
          {informacion.map(i => <NavItem key={i.to} to={i.to} icon={i.icon} label={i.label} />)}
        </>}

        {gestion.length > 0 && <>
          <SectionLabel>Gestión</SectionLabel>
          {gestion.map(i => <NavItem key={i.to} to={i.to} icon={i.icon} label={i.label} />)}
        </>}
      </nav>

      {/* Footer */}
      <div style={{ padding: '4px 8px 8px', borderTop: `1px solid ${BORDER}`, flexShrink: 0 }}>
        <NavItem to="/perfil"        icon={User}     label="Mi Perfil" />
        <NavItem to="/configuracion" icon={Settings} label="Configuración" />
        <button
          onClick={handleLogout}
          style={{
            display: 'flex', alignItems: 'center', gap: '8px',
            padding: '5px 10px', borderRadius: '6px', width: '100%',
            background: 'transparent', border: 'none', cursor: 'pointer',
            fontSize: '0.8rem', color: '#ef4444', textAlign: 'left',
            transition: 'all 0.12s', marginTop: '1px',
          }}
          onMouseEnter={e => e.currentTarget.style.background = 'rgba(239,68,68,0.1)'}
          onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
        >
          <span style={{ flexShrink: 0, width: 16, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <LogOut size={14} strokeWidth={1.8} />
          </span>
          Cerrar Sesión
        </button>
      </div>
    </aside>
    </>
  );
}
